// src/calendar/AvailabilityView.js
//
// The "Availability" tab shared by pages/Calendar.js and
// calendar/EmbeddedCalendar.js.
//
//   - Week strip on top: 7 day chips with an open-block count.
//   - Day grid below: one column per tech, one row per 2-hour block, plus an
//     All Day row for ANY-time and multi-day (construction) jobs.
//   - Red tile  = booked  -> onOpenJob(job)   (parent shows its detail popup)
//   - Green tile = open   -> onScheduleSlot({ dateKey, startTime, techName })
//                            (parent opens the Scheduling "Schedule Job" popup)
//
// All slot rules live in utils/availabilitySlots.js.

import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { filterStaffForDropdowns } from "../utils/staffSettingsUtils";
import {
  AVAILABILITY_SLOTS,
  buildDayAvailability,
  describeJobForTile,
  getJobTechNames,
  normalizeName,
} from "../utils/availabilitySlots";
import "./AvailabilityView.css";

const TECH_SELECTION_KEY = "availability.techs.v1";
const DEFAULT_ROLES = new Set(["tech", "subcontractor"]);

const readStoredTechs = () => {
  try {
    const raw = window.localStorage.getItem(TECH_SELECTION_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredTechs = (names) => {
  try {
    window.localStorage.setItem(TECH_SELECTION_KEY, JSON.stringify(names));
  } catch {
    /* storage unavailable: selection just won't persist */
  }
};

const staffName = (u = {}) => `${u.firstName || ""} ${u.lastName || ""}`.trim();

const BookedTile = ({ job, dateKey, onOpenJob, compact = false }) => (
  <button
    type="button"
    className={`av-tile av-booked${compact ? " av-compact" : ""}`}
    onClick={() => onOpenJob(job)}
    title="View job details"
  >
    {job.wo ? <span className="av-wo">WO# {job.wo}</span> : null}
    <span>{describeJobForTile(job, dateKey)}</span>
  </button>
);

const AvailabilityView = ({
  selectedDate,
  onSelectDate,
  jobsByDate = {},
  timeOffByDate = {},
  usersList = [],
  onOpenJob,
  onScheduleSlot,
}) => {
  const [showTechPicker, setShowTechPicker] = useState(false);
  const [selectedTechs, setSelectedTechs] = useState(() => readStoredTechs());
  const [now, setNow] = useState(() => new Date());

  // Keep "past" blocks current if the page stays open.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const dateKey = moment(selectedDate).format("YYYY-MM-DD");
  const weekStart = moment(selectedDate).day(0).startOf("day");
  const weekKeys = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, "days").format("YYYY-MM-DD")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekStart.format("YYYY-MM-DD")]
  );

  // Everyone who can be assigned a job (same list as the Tech Assign dropdown).
  const roster = useMemo(() => {
    const seen = new Set();
    return filterStaffForDropdowns(usersList)
      .map((u) => ({ name: staffName(u), role: String(u.role || "").toLowerCase() }))
      .filter((u) => {
        const key = normalizeName(u.name);
        if (!u.name || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [usersList]);

  // Default columns: techs/subcontractors plus anyone with a job this week.
  const defaultTechs = useMemo(() => {
    const busy = new Set();
    weekKeys.forEach((k) =>
      (jobsByDate[k] || []).forEach((j) => getJobTechNames(j).forEach((n) => busy.add(normalizeName(n))))
    );
    const picked = roster
      .filter((u) => DEFAULT_ROLES.has(u.role) || busy.has(normalizeName(u.name)))
      .map((u) => u.name);
    return picked.length ? picked : roster.map((u) => u.name);
  }, [roster, jobsByDate, weekKeys]);

  const visibleTechs = useMemo(() => {
    if (!selectedTechs) return defaultTechs;
    const wanted = new Set(selectedTechs.map(normalizeName));
    return roster.map((u) => u.name).filter((n) => wanted.has(normalizeName(n)));
  }, [selectedTechs, defaultTechs, roster]);

  const toggleTech = (name) => {
    const current = visibleTechs;
    const key = normalizeName(name);
    const next = current.some((n) => normalizeName(n) === key)
      ? current.filter((n) => normalizeName(n) !== key)
      : [...current, name];
    setSelectedTechs(next);
    writeStoredTechs(next);
  };

  const resetTechs = () => {
    setSelectedTechs(null);
    try { window.localStorage.removeItem(TECH_SELECTION_KEY); } catch { /* ignore */ }
  };

  const weekSummary = useMemo(
    () =>
      weekKeys.map((k) => ({
        dateKey: k,
        openCount: buildDayAvailability({
          jobs: jobsByDate[k] || [],
          techs: visibleTechs,
          timeOff: timeOffByDate[k] || [],
          dateKey: k,
          now,
        }).openCount,
        jobCount: (jobsByDate[k] || []).length,
      })),
    [weekKeys, jobsByDate, timeOffByDate, visibleTechs, now]
  );

  const day = useMemo(
    () =>
      buildDayAvailability({
        jobs: jobsByDate[dateKey] || [],
        techs: visibleTechs,
        timeOff: timeOffByDate[dateKey] || [],
        dateKey,
        now,
      }),
    [jobsByDate, timeOffByDate, visibleTechs, dateKey, now]
  );

  const shiftWeek = (dir) => onSelectDate(moment(selectedDate).add(dir * 7, "days").toDate());
  const todayKey = moment(now).format("YYYY-MM-DD");
  const unassignedJobs = [...day.unassigned.allDayJobs, ...day.unassigned.timedJobs];
  const gridColumns = `84px repeat(${Math.max(day.columns.length, 1)}, minmax(170px, 1fr))`;

  return (
    <div className="availability">
      <div className="av-toolbar">
        <button type="button" className="av-nav" onClick={() => shiftWeek(-1)} aria-label="Previous week">
          ‹ Prev
        </button>
        <div className="av-range">
          {weekStart.format("MMM D")} – {weekStart.clone().add(6, "days").format("MMM D, YYYY")}
        </div>
        <button type="button" className="av-nav" onClick={() => shiftWeek(1)} aria-label="Next week">
          Next ›
        </button>
        <input
          type="date"
          className="av-date"
          value={dateKey}
          onChange={(e) => e.target.value && onSelectDate(moment(e.target.value, "YYYY-MM-DD").toDate())}
        />
        <button
          type="button"
          className="av-techs-btn"
          onClick={() => setShowTechPicker((v) => !v)}
          aria-expanded={showTechPicker}
        >
          Techs ({visibleTechs.length})
        </button>
      </div>

      {showTechPicker && (
        <div className="av-tech-picker">
          {roster.length === 0 && <span className="av-muted">Loading staff…</span>}
          {roster.map((u) => {
            const on = visibleTechs.some((n) => normalizeName(n) === normalizeName(u.name));
            return (
              <button
                type="button"
                key={u.name}
                className={`av-chip${on ? " on" : ""}`}
                onClick={() => toggleTech(u.name)}
                aria-pressed={on}
              >
                {u.name}
              </button>
            );
          })}
          {roster.length > 0 && (
            <button type="button" className="av-link" onClick={resetTechs}>
              Reset to default
            </button>
          )}
        </div>
      )}

      <div className="av-week" role="tablist" aria-label="Days of the week">
        {weekSummary.map(({ dateKey: k, openCount, jobCount }) => {
          const m = moment(k, "YYYY-MM-DD");
          const active = k === dateKey;
          return (
            <button
              type="button"
              key={k}
              role="tab"
              aria-selected={active}
              className={`av-day${active ? " active" : ""}${k === todayKey ? " today" : ""}${k < todayKey ? " past" : ""}`}
              onClick={() => onSelectDate(m.toDate())}
            >
              <span className="av-day-name">{m.format("ddd")}</span>
              <span className="av-day-date">{m.format("M/D")}</span>
              <span className={`av-day-open${openCount ? "" : " none"}`}>
                {k < todayKey ? `${jobCount} job${jobCount === 1 ? "" : "s"}` : `${openCount} open`}
              </span>
            </button>
          );
        })}
      </div>

      <div className="av-legend">
        <span><i className="sw open" /> Open — click to schedule</span>
        <span><i className="sw booked" /> Booked — click for details</span>
        <span><i className="sw off" /> Time off</span>
        <span><i className="sw past" /> Past</span>
      </div>

      <h3 className="av-heading">{moment(selectedDate).format("dddd, MMMM D, YYYY")}</h3>

      {unassignedJobs.length > 0 && (
        <div className="av-unassigned">
          <div className="av-unassigned-label">No tech assigned</div>
          <div className="av-unassigned-list">
            {unassignedJobs.map((job, i) => (
              <BookedTile key={`${job.id}-${i}`} job={job} dateKey={dateKey} onOpenJob={onOpenJob} compact />
            ))}
          </div>
        </div>
      )}

      {visibleTechs.length === 0 ? (
        <div className="av-empty">
          {roster.length === 0 ? "Loading staff…" : "No techs selected. Click Techs to pick who to show."}
        </div>
      ) : (
        <div className="av-scroll">
          <div className="av-grid" style={{ gridTemplateColumns: gridColumns }}>
            {/* Header */}
            <div className="av-corner" />
            {day.columns.map((col) => (
              <div key={`h-${col.name}`} className="av-tech-head">
                {col.name}
              </div>
            ))}

            {/* All Day row */}
            <div className="av-time">All Day</div>
            {day.columns.map((col) => (
              <div key={`ad-${col.name}`} className="av-cell av-allday-cell">
                {col.allDayJobs.map((job, i) => (
                  <BookedTile key={`${job.id}-ad-${i}`} job={job} dateKey={dateKey} onOpenJob={onOpenJob} />
                ))}
                {col.otherTimeJobs.map((job, i) => (
                  <BookedTile key={`${job.id}-ot-${i}`} job={job} dateKey={dateKey} onOpenJob={onOpenJob} compact />
                ))}
                {col.timeOff.map((entry, i) => (
                  <div key={`${entry.id || i}-off`} className="av-tile av-off av-compact">
                    {entry.dayType === "leaveEarly"
                      ? "Leaving early"
                      : entry.dayType === "arriveLate"
                      ? "Coming in late"
                      : "Time off"}
                  </div>
                ))}
                {col.canScheduleAllDay && (
                  <button
                    type="button"
                    className="av-tile av-open av-compact"
                    onClick={() => onScheduleSlot({ dateKey, startTime: "ANY", techName: col.name })}
                    title="Schedule an all-day job (construction)"
                  >
                    + All day job
                  </button>
                )}
              </div>
            ))}

            {/* 2-hour blocks */}
            {AVAILABILITY_SLOTS.map((slot, slotIndex) => (
              <React.Fragment key={slot.key}>
                <div className="av-time">{slot.label}</div>
                {day.columns.map((col) => {
                  const cell = col.slots[slotIndex];
                  return (
                    <div key={`${slot.key}-${col.name}`} className="av-cell">
                      {cell.status === "booked" &&
                        cell.jobs.map((job, i) => (
                          <BookedTile key={`${job.id}-${i}`} job={job} dateKey={dateKey} onOpenJob={onOpenJob} />
                        ))}
                      {cell.status === "allday" && (
                        <button
                          type="button"
                          className="av-tile av-booked av-faded"
                          onClick={() => onOpenJob(col.allDayJobs[0])}
                          title="Booked all day — view job"
                        >
                          Booked all day
                        </button>
                      )}
                      {cell.status === "off" && <div className="av-tile av-off">Time off</div>}
                      {cell.status === "past" && <div className="av-tile av-past">—</div>}
                      {cell.status === "open" && (
                        <button
                          type="button"
                          className="av-tile av-open"
                          onClick={() => onScheduleSlot({ dateKey, startTime: slot.key, techName: col.name })}
                          title={`Schedule ${col.name}, ${slot.key}`}
                        >
                          <strong>Open</strong>
                          <span className="av-open-sub">{slot.key}</span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityView;
